import BikeColor from "../BikeColor";
import PropTypes from "prop-types";
import { Box, Chip, IconButton, Tooltip, alpha } from "@mui/material";
import { Phone, Edit, Delete, DirectionsBike, CheckCircle, Cancel } from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

// ============ CELDAS USUARIOS ============
const IdCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="bold" color="info">
    #{value}
  </MDTypography>
);

IdCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const IdentificationCell = ({ row }) => {
  const originalRow = row?.original || row;
  return (
    <MDBox>
      <MDTypography variant="caption" display="block" fontWeight="500" sx={{ color: "#1e293b" }}>
        {originalRow?.identification || "N/A"}
      </MDTypography>
      <MDTypography
        variant="caption"
        display="block"
        sx={{ color: "#64748b", fontSize: "0.65rem" }}
      >
        Tipo: {originalRow?.id_type || "—"}
      </MDTypography>
    </MDBox>
  );
};

const NameCell = ({ row }) => {
  const originalRow = row?.original || row;
  return (
    <MDBox>
      <MDTypography
        variant="caption"
        display="block"
        fontWeight="600"
        sx={{ color: "#1e293b", fontSize: "0.8rem" }}
      >
        {originalRow?.names} {originalRow?.lastnames}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        {originalRow?.more || "—"}
      </MDTypography>
    </MDBox>
  );
};

const PhoneCell = ({ row }) => {
  const originalRow = row?.original || row;
  const phone = originalRow?.more || originalRow?.phone || "";

  if (!phone) {
    return (
      <MDTypography variant="caption" sx={{ color: "#94a3b8" }}>
        —
      </MDTypography>
    );
  }
  return (
    <MDBox display="flex" alignItems="center" gap={0.5}>
      <Phone sx={{ fontSize: 14, color: "#5c6bc0" }} />
      <MDTypography variant="caption" sx={{ fontSize: "0.75rem" }}>
        {phone}
      </MDTypography>
    </MDBox>
  );
};

const StatusCell = ({ value }) => {
  const isActive = Number(value) === 1;
  return (
    <Chip
      size="small"
      icon={isActive ? <CheckCircle /> : <Cancel />}
      label={isActive ? "ACTIVO" : "INACTIVO"}
      sx={{
        height: 22,
        fontSize: "0.65rem",
        fontWeight: "bold",
        bgcolor: isActive ? "#e8f5e9" : "#ffebee",
        color: isActive ? "#2e7d32" : "#c62828",
        "& .MuiChip-icon": {
          color: isActive ? "#2e7d32" : "#c62828",
          fontSize: 14,
        },
      }}
    />
  );
};

const DateCell = ({ value }) => {
  if (!value) return <MDTypography variant="caption">N/A</MDTypography>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return <MDTypography variant="caption">N/A</MDTypography>;
  return (
    <MDTypography variant="caption" sx={{ fontSize: "0.7rem" }}>
      {d.toLocaleDateString("es-CO")}
    </MDTypography>
  );
};

// ============ CELDA DE ACCIONES ============
const ActionsCell = ({ row, onEdit, onDelete, onViewBikes, bikesCount }) => {
  const originalRow = row?.original || row;

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) onEdit(originalRow);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) onDelete(originalRow);
  };

  const handleViewBikes = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onViewBikes) onViewBikes(originalRow);
  };

  return (
    <MDBox display="flex" gap={0.5} justifyContent="flex-end">
      <Tooltip title={`Ver bicicletas (${bikesCount})`} arrow>
        <IconButton
          size="small"
          onClick={handleViewBikes}
          sx={{
            bgcolor: alpha("#009688", 0.08),
            color: "#009688",
            "&:hover": { bgcolor: alpha("#009688", 0.15) },
            position: "relative",
          }}
        >
          <DirectionsBike sx={{ fontSize: 16 }} />
          {bikesCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: -4,
                right: -4,
                bgcolor: "#009688",
                color: "#fff",
                fontSize: "0.55rem",
                fontWeight: 700,
                borderRadius: "50%",
                minWidth: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.5,
              }}
            >
              {bikesCount}
            </Box>
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar usuario" arrow>
        <IconButton
          size="small"
          onClick={handleEdit}
          sx={{
            bgcolor: alpha("#1976d2", 0.08),
            color: "#1976d2",
            "&:hover": { bgcolor: alpha("#1976d2", 0.15) },
          }}
        >
          <Edit sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar usuario" arrow>
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{
            bgcolor: alpha("#c62828", 0.08),
            color: "#c62828",
            "&:hover": { bgcolor: alpha("#c62828", 0.15) },
          }}
        >
          <Delete sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </MDBox>
  );
};

// ============ CELDAS DE BICICLETAS ============
const BikeIdCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="bold" color="info">
    #{value}
  </MDTypography>
);

const BikeCodeCell = ({ row }) => {
  const originalRow = row?.original || row;
  return (
    <MDTypography
      variant="caption"
      display="block"
      fontWeight="600"
      sx={{ color: "#1e293b", fontSize: "0.8rem" }}
    >
      {originalRow?.code || "—"}
    </MDTypography>
  );
};

const BikeColorCell = ({ row }) => {
  const originalRow = row?.original || row;
  const color = originalRow?.color || "";
  return <BikeColor value={color} />;
};

const BikeActionsCell = ({ row, onEdit, onDelete }) => {
  const originalRow = row?.original || row;

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) onEdit(originalRow);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) onDelete(originalRow);
  };

  return (
    <MDBox display="flex" gap={0.5} justifyContent="flex-end">
      <Tooltip title="Editar bicicleta" arrow>
        <IconButton
          size="small"
          onClick={handleEdit}
          sx={{
            bgcolor: alpha("#1976d2", 0.08),
            color: "#1976d2",
            "&:hover": { bgcolor: alpha("#1976d2", 0.15) },
          }}
        >
          <Edit sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar bicicleta" arrow>
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{
            bgcolor: alpha("#c62828", 0.08),
            color: "#c62828",
            "&:hover": { bgcolor: alpha("#c62828", 0.15) },
          }}
        >
          <Delete sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </MDBox>
  );
};

IdentificationCell.propTypes = { row: PropTypes.object };
NameCell.propTypes = { row: PropTypes.object };
PhoneCell.propTypes = { row: PropTypes.object };
StatusCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};
DateCell.propTypes = { value: PropTypes.string };
ActionsCell.propTypes = {
  row: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewBikes: PropTypes.func.isRequired,
  bikesCount: PropTypes.number,
};
BikeIdCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };
BikeCodeCell.propTypes = { row: PropTypes.object };
BikeColorCell.propTypes = { row: PropTypes.object };
BikeActionsCell.propTypes = {
  row: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export {
  IdCell,
  IdentificationCell,
  NameCell,
  PhoneCell,
  StatusCell,
  DateCell,
  ActionsCell,
  BikeIdCell,
  BikeCodeCell,
  BikeColorCell,
  BikeActionsCell,
};
